import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * EditRequestsIndicator
 * 
 * Small dashboard card showing pending edit requests count.
 * Links to the full Edit Requests page in Team section.
 * Only visible to Admin/Team Leaders.
 */
const EditRequestsIndicator = ({ currentUser }) => {
  const navigate = useNavigate();
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchPendingCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/edit-requests/pending-count`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPendingCount(response.data.count || 0);
    } catch (err) {
      console.error('Error fetching edit requests count:', err);
      setPendingCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingCount();
    // Refresh every 30 seconds
    const interval = setInterval(fetchPendingCount, 30000);
    return () => clearInterval(interval);
  }, [fetchPendingCount]);

  // Only show for Admin/Team Leaders
  const canView = ['admin', 'tax_search_team_leader', 'title_search_team_leader', 'team_leader'].includes(currentUser?.role);
  
  if (!canView) return null;
  
  // Don't show while loading or if no pending requests
  if (loading || pendingCount === 0) return null;

  return (
    <div 
      onClick={() => navigate('/team/edit-requests')}
      className="bg-white rounded-xl border border-amber-200 shadow-sm p-4 cursor-pointer hover:shadow-md hover:border-amber-300 transition-all duration-200 mb-4"
      data-testid="edit-requests-indicator"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
            <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">Edit Requests</p>
            <p className="text-xs text-gray-500">From Order Desk</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <span className="px-3 py-1 bg-amber-500 text-white text-sm font-bold rounded-full">
              {pendingCount} Pending
            </span>
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditRequestsIndicator;
